<template>
  <div class="bg-white rounded-lg border border-gray-200 overflow-hidden mb-12">
    <ComponentHeader title="Role Configurations">
        <div class="flex items-center gap-2">
          <ExportMenu :disabled="!displayRoles.length" :exports="[{ type: 'excel', action: exportToExcel }]" />
          <button
            v-if="canAddFeature && addableFeatures.length > 0"
            class="btn-secondary-sm"
            @click="openAddFeatureModal"
          >
            <font-awesome-icon :icon="['fas', 'plus']" /> Add Feature
          </button>
          <button
            v-if="canCreateRole"
            :class="{ 'btn-primary-sm': !isDirty, 'btn-secondary-sm': isDirty }"
            @click="openCreateRoleModal"
          >
            <font-awesome-icon :icon="['fas', 'plus']" /> Create Role
          </button>
          <button
            v-if="isDirty"
            class="btn-secondary-sm"
            @click="discardChanges"
          >
            <font-awesome-icon :icon="['fas', 'times']" />
            Discard
          </button>
          <button
            v-if="isDirty && canEditRoles"
            class="btn-primary-sm"
            :disabled="saving"
            @click="saveChanges"
          >
            <font-awesome-icon :icon="['fas', 'floppy-disk']" /> {{ saving ? 'Saving...' : 'Save Changes' }}
          </button>
        </div>
    </ComponentHeader>
    <div class="overflow-x-auto">
      <table class="min-w-full divide-y divide-gray-200">
        <template v-for="featureGroup in groupedFeatures" :key="featureGroup.category">
          <thead class="bg-gray-50">
            <tr>
              <th class="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider">
                {{ featureGroup.category_name }}
              </th>
              <th
                v-for="role in displayRoles"
                :key="role.role_id"
                :class="[
                  'px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider text-center group/role',
                  canEditRoles ? 'cursor-pointer hover:bg-gray-100' : '',
                ]"
                :title="canEditRoles ? 'Edit Role' : ''"
                @click="canEditRoles ? openEditRoleModal(role) : null"
              >
                <div class="flex flex-col items-center gap-1">
                  <div class="flex items-center gap-1">
                    <div>
                      <div class="leading-tight">{{ role.display_name }}</div>
                      <span
                        v-if="role.role_id === 'security'"
                        class="mt-1 px-1.5 py-0.5 text-[10px] font-semibold rounded-full bg-red-100 text-red-700 normal-case tracking-normal leading-none inline-block"
                      >Security</span>
                      <span
                        v-else-if="role.role_id === 'admin'"
                        class="mt-1 px-1.5 py-0.5 text-[10px] font-semibold rounded-full bg-blue-100 text-blue-700 normal-case tracking-normal leading-none inline-block"
                      >Admin</span>
                    </div>
                    <font-awesome-icon
                      v-if="canEditRoles"
                      :icon="['fas', 'pencil']"
                      class="w-3 h-3 text-gray-400 opacity-0 group-hover/role:opacity-100 transition-opacity"
                    />
                  </div>
                  
                </div>
              </th>
            </tr>
          </thead>
          <tbody class="bg-white divide-y divide-gray-200">
            <template v-for="feature in featureGroup.features" :key="feature.id">
              <tr style="vertical-align: middle;">
                <td
                  :class="['px-4 py-2 whitespace-nowrap text-sm font-medium text-gray-900', feature.children && feature.children.length ? 'cursor-pointer select-none' : '']"
                  :title="feature.description"
                  @click="feature.children && feature.children.length ? toggleExpanded(feature.id) : null"
                >
                  <font-awesome-icon
                    v-if="feature.children && feature.children.length"
                    :icon="['fas', expandedFeatures[feature.id] ? 'chevron-down' : 'chevron-right']"
                    class="w-3 h-3 text-gray-400 mr-1"
                  />
                  {{ feature.name }}
                  <span
                    v-if="feature.is_beta"
                    class="ml-2 px-1.5 py-0.5 text-[10px] font-semibold rounded-full bg-purple-100 text-purple-700 normal-case tracking-normal leading-none inline-block"
                    title="Beta feature — hidden from non-superuser views"
                  >Beta</span>
                </td>
                <td
                  v-for="role in displayRoles"
                  :key="role.role_id"
                  :class="[
                    'px-4 py-1 text-sm text-gray-500 text-center',
                    isFeatureChanged(role.role_id, feature.id) ? 'bg-amber-100' : '',
                  ]"
                  style="line-height: 1em;"
                >
                  <input
                    type="checkbox"
                    :class="[
                      'h-4 w-4',
                      !canEditRoles ? 'pointer-events-none' : 'cursor-pointer',
                    ]"
                    :checked="isFeatureChecked(role.role_id, feature.id)"
                    :disabled="!canEditRoles"
                    @change="toggleFeature(role.role_id, feature.id)"
                  />
                </td>
              </tr>
              <!-- Children (nested features) -->
              <tr
                v-for="child in feature.children"
                v-show="expandedFeatures[feature.id]"
                :key="child.id"
                style="vertical-align: middle;"
              >
                <td
                  class="px-4 py-2 whitespace-nowrap text-sm font-medium text-gray-900 ps-7"
                  :title="child.description"
                >
                  {{ child.name }}
                  <span
                    v-if="child.is_beta"
                    class="ml-2 px-1.5 py-0.5 text-[10px] font-semibold rounded-full bg-purple-100 text-purple-700 normal-case tracking-normal leading-none inline-block"
                    title="Beta feature — hidden from non-superuser views"
                  >Beta</span>
                </td>
                <td
                  v-for="role in displayRoles"
                  :key="role.role_id"
                  :class="[
                    'px-4 py-1 text-sm text-gray-500 text-center',
                    isFeatureChanged(role.role_id, child.id) ? 'bg-amber-50' : '',
                  ]"
                  style="line-height: 1em;"
                >
                  <input
                    type="checkbox"
                    :class="[
                      'h-4 w-4',
                      !canEditRoles ? 'pointer-events-none' : 'cursor-pointer',
                    ]"
                    :checked="isFeatureChecked(role.role_id, child.id)"
                    :disabled="!canEditRoles"
                    @change="toggleFeature(role.role_id, child.id)"
                  />
                </td>
              </tr>
            </template>
          </tbody>
        </template>
      </table>
    </div>

    <!-- Add Feature Modal (superuser-only) -->
    <div
      v-if="showAddFeatureModal"
      class="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
      @click.self="closeAddFeatureModal"
    >
      <div class="bg-white rounded-lg p-6 w-[32rem] shadow-xl">
        <h3 class="text-lg font-semibold mb-4">Add Feature to Tenant</h3>
        <p class="text-sm text-gray-600 mb-4">
          Enable features for this tenant. Enabled features appear in the role
          matrix and can be granted to roles.
        </p>
        <div class="space-y-2 max-h-96 overflow-y-auto border border-gray-200 rounded p-3">
          <label
            v-for="feat in addableFeatures"
            :key="feat.id"
            class="flex items-start gap-2 text-sm cursor-pointer hover:bg-gray-50 p-2 rounded"
          >
            <input
              type="checkbox"
              :value="feat.id"
              v-model="selectedAddFeatureIds"
              class="mt-0.5"
            />
            <div class="flex-1">
              <div class="font-medium text-gray-900 flex items-center gap-2">
                {{ feat.name }}
                <span
                  v-if="feat.is_beta"
                  class="px-1.5 py-0.5 text-[10px] font-semibold rounded-full bg-purple-100 text-purple-700"
                >Beta</span>
                <span class="text-xs text-gray-400">({{ feat.category_name }})</span>
              </div>
              <div v-if="feat.description" class="text-xs text-gray-500 mt-0.5">
                {{ feat.description }}
              </div>
            </div>
          </label>
        </div>
        <p v-if="addFeatureError" class="text-red-600 text-sm mt-2">{{ addFeatureError }}</p>
        <div class="flex justify-end gap-2 mt-4">
          <button class="btn-secondary text-sm" @click="closeAddFeatureModal">Cancel</button>
          <button
            class="btn-primary text-sm"
            :disabled="selectedAddFeatureIds.length === 0 || addFeatureSaving"
            @click="submitAddFeature"
          >
            {{ addFeatureSaving ? 'Saving...' : `Add ${selectedAddFeatureIds.length || ''}` }}
          </button>
        </div>
      </div>
    </div>

    <!-- Role Modal (Add / Edit) -->
    <div
      v-if="roleModalMode"
      class="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
      @click.self="closeRoleModal"
    >
      <div class="bg-white rounded-lg p-6 w-96 shadow-xl">
        <h3 class="text-lg font-semibold mb-4 flex items-center gap-2">
          <span>{{ roleModalMode === 'create' ? 'Create Role' : 'Edit Role' }}</span>
          <span
            v-if="editingRole?.role_id === 'security'"
            class="px-2 py-0.5 text-[10px] font-semibold rounded-full bg-red-100 text-red-700 normal-case tracking-normal leading-none inline-block"
          >Security</span>
          <span
            v-else-if="editingRole?.role_id === 'admin'"
            class="px-2 py-0.5 text-[10px] font-semibold rounded-full bg-blue-100 text-blue-700 normal-case tracking-normal leading-none inline-block"
          >Admin</span>
        </h3>
        <div class="space-y-4">
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Display Name</label>
            <input
              v-model="roleForm.display_name"
              type="text"
              class="w-full border border-gray-300 rounded px-3 py-2 text-sm"
              placeholder="e.g. Custom Role"
            />
          </div>
          <div v-if="showAccessDesignation">
            <label class="block text-sm font-medium text-gray-700 mb-2">API Access Designation</label>
            <div class="space-y-2">
              <label class="flex items-center gap-2 cursor-pointer text-sm">
                <input
                  type="radio"
                  class="h-4 w-4"
                  value="none"
                  :checked="roleForm.access_designation === 'none'"
                  @change="roleForm.access_designation = 'none'"
                />
                None
              </label>
              <label :class="['flex items-center gap-2 text-sm', designationTaken('security') ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer']">
                <input
                  type="radio"
                  class="h-4 w-4"
                  value="security"
                  :checked="roleForm.access_designation === 'security'"
                  :disabled="designationTaken('security')"
                  @change="roleForm.access_designation = 'security'"
                />
                Security
                <span v-if="designationTaken('security')" class="text-xs text-gray-400">(assigned)</span>
              </label>
              <label :class="['flex items-center gap-2 text-sm', designationTaken('admin') ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer']">
                <input
                  type="radio"
                  class="h-4 w-4"
                  value="admin"
                  :checked="roleForm.access_designation === 'admin'"
                  :disabled="designationTaken('admin')"
                  @change="roleForm.access_designation = 'admin'"
                />
                Admin
                <span v-if="designationTaken('admin')" class="text-xs text-gray-400">(assigned)</span>
              </label>
            </div>
          </div>
          <p v-if="roleFormError" class="text-red-600 text-sm">{{ roleFormError }}</p>
        </div>
        <div class="flex justify-between items-center mt-6">
          <button
            v-if="roleModalMode === 'edit' && canCreateRole && canDeleteEditingRole"
            class="text-red-500 hover:text-red-700 text-sm transition-colors"
            @click="showDeleteConfirm = true"
          >
            <font-awesome-icon :icon="['fas', 'trash-can']" class="mr-1" />
            Delete
          </button>
          <span v-else></span>
          <div class="flex gap-2">
            <button class="btn-secondary text-sm" @click="closeRoleModal">Cancel</button>
            <button class="btn-primary text-sm" :disabled="roleFormSaving" @click="submitRoleForm">
              {{ roleFormSaving ? 'Saving...' : (roleModalMode === 'create' ? 'Create' : 'Save') }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Delete Role Confirmation -->
    <ConfirmationModal
      :is-visible="showDeleteConfirm"
      :message="`Are you sure you want to delete '${editingRole?.display_name}'? This will also remove all feature mappings for this role.`"
      @confirm="deleteRole"
      @cancel="showDeleteConfirm = false"
    />
  </div>
</template>

<script>
import axios from 'axios';
import { mapGetters } from 'vuex';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { canAccessFeature } from '@/utils/access';
import { FEATURES } from '@/constants/features';
import ComponentHeader from '@/components/widgetcomponents/library/ComponentHeader.vue';
import ExportMenu from '@/components/widgetcomponents/library/ExportMenu.vue';
import { exportToExcel } from '@/utils/exportArraytoXlsx.js';
import ConfirmationModal from '@/components/widgetcomponents/ConfirmationModal.vue';
import { RESERVED_ROLE_IDS } from '@/constants/roles';

export default {
  name: 'ConfigRoles',
  components: {
    ComponentHeader,
    ExportMenu,
    ConfirmationModal,
    FontAwesomeIcon,
  },
  data() {
    return {
      localRoleFeatures: {},
      saving: false,
      expandedFeatures: {},
      roleModalMode: null,
      editingRole: null,
      roleForm: { display_name: '', access_designation: 'none' },
      roleFormError: '',
      roleFormSaving: false,
      showDeleteConfirm: false,
      showAddFeatureModal: false,
      selectedAddFeatureIds: [],
      addFeatureError: '',
      addFeatureSaving: false,
    };
  },
  computed: {
    ...mapGetters('tenant', ['current_tenant_slug']),
    ...mapGetters('auth', ['current_user']),
    // `roles` is the full list including hide_from_config — needed by
    // designationTaken() so a legacy hidden role still counts as holding
    // its designation. `visibleRoles` is the subset rendered in the matrix.
    ...mapGetters('featureConfig', ['features', 'roles', 'roleFeatures', 'addableFeatures', 'visibleRoles']),
    displayRoles() {
      // Role columns rendered in the matrix — hidden roles (hide_from_config)
      // are stored for legacy assignment but shouldn't appear for editing.
      return this.visibleRoles;
    },
    groupedFeatures() {
      return this.features;
    },
    exportRoles() {
      // Excel export uses the same visible set but in { id, name } shape.
      return this.visibleRoles.map(r => ({ id: r.role_id, name: r.display_name }));
    },
    canEditRoles() {
      return canAccessFeature(FEATURES.ACTION_ROLE_CONFIG);
    },
    canCreateRole() {
      return this.current_user?.is_superuser ||
        this.$store.getters['tenant/isSecurityUser'];
    },
    canAddFeature() {
      // Enabling new features for a tenant is superuser-only.
      return this.current_user?.is_superuser;
    },
    canDeleteEditingRole() {
      return this.editingRole && !RESERVED_ROLE_IDS.includes(this.editingRole.role_id);
    },
    showAccessDesignation() {
      // Hide when editing a reserved role — the backend rejects role_id
      // changes on reserved roles, so the picker would be cosmetic.
      if (this.editingRole && RESERVED_ROLE_IDS.includes(this.editingRole.role_id)) {
        return false;
      }
      // Hide once every reserved role has already been assigned — at that
      // point every option except "None" is disabled, so the section is noise.
      return RESERVED_ROLE_IDS.some(r => !this.designationTaken(r));
    },
    isDirty() {
      return JSON.stringify(this.localRoleFeatures) !== JSON.stringify(this.roleFeatures);
    },
  },
  watch: {
    roleFeatures: {
      handler(val) {
        this.localRoleFeatures = JSON.parse(JSON.stringify(val));
      },
      immediate: true,
      deep: true,
    },
    current_tenant_slug() {
      // Tenant switch while on this view → refresh the config for the new tenant.
      this.$store.dispatch('featureConfig/fetchConfig');
    },
  },
  mounted() {
    this.$store.dispatch('featureConfig/fetchConfig');
  },
  methods: {
    exportToExcel() {
      const roles = this.exportRoles;
      const rows = [];
      for (const group of this.groupedFeatures) {
        for (const feature of group.features) {
          const row = { Category: group.category_name, Feature: feature.name };
          for (const role of roles) {
            row[role.name] = this.roleFeatures[role.id]?.includes(feature.id) ? 'Yes' : '';
          }
          rows.push(row);
        }
      }
      const columnWidths = { Category: 160, Feature: 200 };
      for (const role of roles) {
        columnWidths[role.name] = 120;
      }
      const today = new Date().toISOString().split('T')[0];
      exportToExcel(rows, `role_configurations_${today}.xlsx`, {
        sheetName: 'Role Configurations',
        columnWidths,
      });
    },
    toggleExpanded(featureId) {
      this.expandedFeatures = { ...this.expandedFeatures, [featureId]: !this.expandedFeatures[featureId] };
    },
    discardChanges() {
      this.localRoleFeatures = JSON.parse(JSON.stringify(this.roleFeatures));
    },
    isFeatureChanged(roleId, featureId) {
      const savedFeats = this.roleFeatures[roleId] || [];
      const localFeats = this.localRoleFeatures[roleId] || [];
      return savedFeats.includes(featureId) !== localFeats.includes(featureId);
    },
    isFeatureChecked(roleId, featureId) {
      const roleFeats = this.localRoleFeatures[roleId] || [];
      return roleFeats.includes(featureId);
    },
    toggleFeature(roleId, featureId) {
      if (!this.localRoleFeatures[roleId]) {
        this.localRoleFeatures[roleId] = [];
      }
      const arr = this.localRoleFeatures[roleId];
      const idx = arr.indexOf(featureId);
      if (idx >= 0) {
        arr.splice(idx, 1);
      } else {
        arr.push(featureId);
      }
    },
    async saveChanges() {
      this.saving = true;
      try {
        await axios.put('/api/features-config/role-features/', {
          role_features: this.localRoleFeatures,
        });
        await this.$store.dispatch('featureConfig/fetchConfig');
      } catch (error) {
        console.error('Failed to save role features:', error);
      } finally {
        this.saving = false;
      }
    },
    async deleteRole() {
      try {
        await axios.delete(`/api/features-config/roles/${this.editingRole.role_id}/`);
        await this.$store.dispatch('featureConfig/fetchConfig');
        this.showDeleteConfirm = false;
        this.closeRoleModal();
      } catch (error) {
        this.showDeleteConfirm = false;
        const data = error.response?.data;
        this.roleFormError =
          data?.error || data?.detail || error.message || 'Failed to delete role.';
      }
    },
    designationTaken(designation) {
      const excludeRoleId = this.editingRole?.role_id;
      return this.roles.some(r => r.role_id === designation && r.role_id !== excludeRoleId);
    },
    openCreateRoleModal() {
      this.roleModalMode = 'create';
      this.editingRole = null;
      this.roleFormError = '';
      this.showDeleteConfirm = false;
      this.roleForm = { display_name: '', access_designation: 'none' };
    },
    openAddFeatureModal() {
      this.selectedAddFeatureIds = [];
      this.addFeatureError = '';
      this.showAddFeatureModal = true;
    },
    closeAddFeatureModal() {
      this.showAddFeatureModal = false;
      this.selectedAddFeatureIds = [];
      this.addFeatureError = '';
    },
    async submitAddFeature() {
      if (this.selectedAddFeatureIds.length === 0) return;
      this.addFeatureSaving = true;
      this.addFeatureError = '';
      try {
        await axios.post('/api/features-config/features/', {
          feature_ids: this.selectedAddFeatureIds,
        });
        await this.$store.dispatch('featureConfig/fetchConfig');
        this.closeAddFeatureModal();
      } catch (error) {
        const data = error.response?.data;
        this.addFeatureError = data?.error || data?.detail || error.message || 'Failed to add features.';
      } finally {
        this.addFeatureSaving = false;
      }
    },
    openEditRoleModal(role) {
      this.roleModalMode = 'edit';
      this.editingRole = role;
      this.roleFormError = '';
      this.showDeleteConfirm = false;
      const designation = RESERVED_ROLE_IDS.includes(role.role_id) ? role.role_id : 'none';
      this.roleForm = {
        display_name: role.display_name,
        access_designation: designation,
      };
    },
    closeRoleModal() {
      this.roleModalMode = null;
      this.editingRole = null;
      this.roleFormError = '';
      this.showDeleteConfirm = false;
    },
    async submitRoleForm() {
      this.roleFormError = '';
      const displayName = this.roleForm.display_name.trim();
      if (!displayName) {
        this.roleFormError = 'Display Name is required.';
        return;
      }
      this.roleFormSaving = true;
      const designation = this.roleForm.access_designation;
      try {
        if (this.roleModalMode === 'create') {
          const payload = { display_name: displayName };
          if (designation !== 'none') {
            payload.role_id = designation;
          }
          await axios.post('/api/features-config/roles/', payload);
        } else {
          // Edit mode: display_name is always editable; role_id is only
          // rewritten when the user picks a designation (security/admin) for
          // a role that didn't previously hold one. Reserved roles hide the
          // designation picker entirely via showAccessDesignation, so there's
          // no path here to undo a reserved role's designation.
          const payload = { display_name: displayName };
          if (designation !== 'none') {
            payload.role_id = designation;
          }
          await axios.patch(`/api/features-config/roles/${this.editingRole.role_id}/update/`, payload);
        }
        await this.$store.dispatch('featureConfig/fetchConfig');
        this.closeRoleModal();
      } catch (error) {
        this.roleFormError = error.response?.data?.error || 'Failed to save role.';
      } finally {
        this.roleFormSaving = false;
      }
    },
  },
};
</script>
